import { useCallback, useMemo, useState } from 'react';
import { ApiOutlined, NodeIndexOutlined } from '@ant-design/icons';

import { DataTable, Tile } from '../../../../components/shared';
import { Badge, Button, Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { KHOI_BANG, LE_BANG } from '../../chung/bang';
import { KhoiLoi } from '../../chung/components/KhoiLoi';
import { LOC_AI_MAC_DINH, thamSoLocLuot, type LocAi } from '../../chung/nghiep-vu/loc-ai';
import { soNguyen } from '../../chung/nghiep-vu/so-thong-ke';
import { TRONG, dayDu, gio } from '../../chung/nghiep-vu/thoi-gian';
import type { LuotGoi, SapXep } from '../../chung/nghiep-vu/types-vet';
import { useDanhSachLuotGoi } from '../hooks';
import { nhanTenant, useNguonVet } from '../nguon';
import { gopLuotGoi, khoangMs } from '../nghiep-vu/vet';
import { BocTachToken } from './BocTachToken';
import { ChanDanhSach } from './ChanDanhSach';
import { ChonSapXep } from './ChonSapXep';
import { ChuoiBuoc } from './ChuoiBuoc';
import { ChiPhi, GiaVon, NhanCanhBao, NhanUocTinh, TietKiemDem } from './NhanTinCay';

/**
 * Từng lượt gọi mô hình — một dòng mỗi lần gọi. Port `vet/BangLuotGoi.tsx` native.
 *
 * Bóc tách bảy loại token nằm trong **hàng mở rộng**, không nằm trong bảy cột: bảy cột số cạnh nhau thì cả bảng thành
 * một ma trận không ai đọc theo hàng, mà câu hỏi thật ở đây là "lượt này tốn ở loại nào" — một câu hỏi về MỘT dòng.
 *
 * `trace_id` là nút: từ một lượt gọi tốn bất thường, việc kế tiếp luôn là xem nó nằm trong chuỗi nào.
 */
export function BangLuotGoi({ tu, den, loc = LOC_AI_MAC_DINH }: { tu: string; den: string; loc?: LocAi }) {
  const { t, locale } = useT();
  const { cotTenant, tenTenant } = useNguonVet();
  const [sapXep, setSapXep] = useState<SapXep>('moi_nhat');
  const [dangXem, setDangXem] = useState<{ traceId: string; tenantId?: string } | null>(null);

  const locLuot = useMemo(() => thamSoLocLuot(loc), [loc]);
  const thamSo = useMemo(() => ({ tu, den, sap_xep: sapXep, ...locLuot }), [tu, den, sapXep, locLuot]);
  const coLoc = Object.keys(locLuot).length > 0;
  const truyVan = useDanhSachLuotGoi(thamSo);
  const dong = useMemo(() => gopLuotGoi(truyVan.data?.pages), [truyVan.data]);
  const coGiaVon = dong.some((r) => r.chi_phi_von !== undefined);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = truyVan;
  const taiThem = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="space-y-4">
      <Tile className={KHOI_BANG}>
        <div className="border-b border-default">
          <ChonSapXep giaTri={sapXep} onDoi={setSapXep} />
        </div>
        {truyVan.isPending ? (
          <Skeleton className="m-4 h-72" />
        ) : truyVan.isError ? (
          <KhoiLoi loi={truyVan.error} onThuLai={() => void truyVan.refetch()} />
        ) : (
          <>
            <DataTable<LuotGoi>
              caption={t('nktvSuDungAi.luotGoi.bang')}
              className={LE_BANG}
              rowKey="event_id"
              size="small"
              dataSource={dong}
              pagination={false}
              expandable={{
                expandedRowRender: (r) => <BocTachToken token={r.token} tongToken={r.tong_token} className="py-1" />,
                expandRowByClick: true,
              }}
              locale={{
                emptyText: coLoc ? (
                  <Empty icon={ApiOutlined} title={t('nktvSuDungAi.danhSach.rongLoc')} />
                ) : (
                  <Empty
                    icon={ApiOutlined}
                    title={t('nktvSuDungAi.luotGoi.rongTieuDe')}
                    description={t('nktvSuDungAi.luotGoi.rongMoTa')}
                  />
                ),
              }}
              columns={[
                {
                  title: t('nktvSuDungAi.luotGoi.cotThoiGian'),
                  width: 92,
                  render: (_v, r) => (
                    <time
                      dateTime={r.thoi_gian}
                      title={dayDu(r.thoi_gian, locale)}
                      className="font-mono text-xs text-content-secondary"
                    >
                      {gio(r.thoi_gian, locale)}
                    </time>
                  ),
                },
                {
                  title: t('nktvSuDungAi.luotGoi.cotMoHinh'),
                  width: 220,
                  render: (_v, r) => (
                    <div className="min-w-0 max-w-[13rem]">
                      <div className="truncate text-sm text-content-primary">{r.mo_hinh ?? TRONG}</div>
                      <div className="truncate text-xs text-content-muted">
                        {[r.nha_cung_cap, r.kieu_api].filter(Boolean).join(' · ') || TRONG}
                      </div>
                    </div>
                  ),
                },
                {
                  title: t('nktvSuDungAi.luotGoi.cotTinhNang'),
                  width: 180,
                  render: (_v, r) => (
                    <div className="min-w-0 max-w-[11rem]">
                      <div className="truncate text-xs text-content-secondary">
                        {r.nhan_tinh_nang ?? r.tinh_nang ?? TRONG}
                      </div>
                      <div className="truncate text-xs text-content-muted">{r.service}</div>
                    </div>
                  ),
                },
                ...(cotTenant
                  ? [
                      {
                        title: t('nktvSuDungAi.cotTenant'),
                        width: 160,
                        render: (_v: unknown, r: LuotGoi) => (
                          <span className="block max-w-[10rem] truncate text-sm text-content-secondary">
                            {nhanTenant(tenTenant, r.tenant_id)}
                          </span>
                        ),
                      },
                    ]
                  : []),
                {
                  title: t('nktvSuDungAi.luotGoi.cotNguoi'),
                  width: 170,
                  render: (_v, r) => (
                    <div className="min-w-0 max-w-[10rem]">
                      <div className="truncate text-sm text-content-secondary">
                        {r.actor_ten ?? t('nktv.nguoi.khongXacDinh')}
                      </div>
                      <div className="truncate text-xs text-content-muted">{r.doi_tuong_ten ?? r.doi_tuong_id ?? ''}</div>
                    </div>
                  ),
                },
                {
                  title: t('nktvSuDungAi.luotGoi.cotToken'),
                  width: 100,
                  align: 'right',
                  render: (_v, r) => (
                    <span className="font-mono text-xs tabular-nums text-content-primary">
                      {soNguyen(r.tong_token, locale)}
                    </span>
                  ),
                },
                {
                  title: t('nktvSuDungAi.luotGoi.cotChiPhi'),
                  width: 170,
                  render: (_v, r) => (
                    <ChiPhi
                      chiPhi={r.chi_phi_ban}
                      donVi={r.don_vi_tien}
                      trangThai={r.trang_thai}
                      thieuTyGia={r.thieu_ty_gia}
                      moHinh={r.mo_hinh}
                    />
                  ),
                },
                ...(coGiaVon
                  ? [
                      {
                        title: t('nktvSuDungAi.cotGiaVon'),
                        width: 130,
                        render: (_v: unknown, r: LuotGoi) => <GiaVon giaTri={r.chi_phi_von} donVi={r.don_vi_tien} />,
                      },
                    ]
                  : []),
                {
                  title: t('nktvSuDungAi.luotGoi.cotTietKiem'),
                  width: 130,
                  render: (_v, r) => <TietKiemDem giaTri={r.tiet_kiem_dem} donVi={r.don_vi_tien} />,
                },
                {
                  title: t('nktvSuDungAi.luotGoi.cotDoTre'),
                  width: 90,
                  align: 'right',
                  render: (_v, r) => (
                    <span className="font-mono text-xs tabular-nums text-content-secondary">
                      {khoangMs(r.do_tre_ms, locale)}
                    </span>
                  ),
                },
                {
                  title: t('nktvSuDungAi.luotGoi.cotTinCay'),
                  width: 160,
                  render: (_v, r) => (
                    <span className="flex flex-wrap items-center gap-1">
                      {r.canh_bao && <NhanCanhBao canhBao={r.canh_bao} />}
                      {r.uoc_tinh === true && <NhanUocTinh />}
                      {r.ket_qua === 'that_bai' && <Badge variant="danger">{t('nktv.ketQua.that_bai')}</Badge>}
                    </span>
                  ),
                },
                {
                  title: t('nktvSuDungAi.luotGoi.cotChuoi'),
                  width: 120,
                  fixed: 'right',
                  render: (_v, r) => {
                    const traceId = r.trace_id;
                    if (traceId === null) return <span className="text-xs text-content-muted">{TRONG}</span>;
                    return (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<NodeIndexOutlined />}
                        onClick={(e) => {
                          // Hàng mở rộng bật theo cú bấm vào dòng — không chặn nổi bọt thì bấm nút này vừa mở ngăn
                          // kéo vừa bung hàng bóc tách.
                          e.stopPropagation();
                          setDangXem({ traceId, tenantId: r.tenant_id });
                        }}
                      >
                        {t('nktvSuDungAi.luotGoi.xemChuoi')}
                      </Button>
                    );
                  },
                },
              ]}
            />

            <ChanDanhSach
              coTrangTiep={hasNextPage === true}
              dangTai={isFetchingNextPage}
              onTaiThem={taiThem}
              soDong={dong.length}
              ghiChu={sapXep === 'ton_nhat' ? t('nktvSuDungAi.danhSach.xepHangMotTrang') : undefined}
            />
          </>
        )}
      </Tile>

      {dangXem !== null && (
        <ChuoiBuoc traceId={dangXem.traceId} tenantId={dangXem.tenantId} onDong={() => setDangXem(null)} />
      )}
    </div>
  );
}
